import type {
  ConfirmUploadParams,
  ConfirmUploadResponse,
  DeleteInitialDataParams,
  DownloadTemplateResponse,
  ExportStatementReportParams,
  ExportStatementReportResponse,
  InitialListCondition,
  InitialListItem,
  QueryUploadPreviewListParams,
  StatementReportListItem,
  StatementReportListParams,
  UpdateInitialDataParams,
  UploadInitialDataResponse,
  UploadPreviewListItem,
} from './types'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { dayjs } from '#/utils'

export const customerReconciliationDailyApi = defineApi(
  'operation',
  ({ prefix }) => {
    /**
     * 下载期初模版
     * GET /gobestads/operation/adsCustomerInitialSet/downloadTemplate
     */
    function downloadTemplate() {
      return requestClient
        .get<DownloadTemplateResponse>(
          prefix('adsCustomerInitialSet/downloadTemplate'),
        )
        .then((res) => {
          return {
            url: res?.url || '',
          }
        })
    }

    /**
     * 上传期初数据
     * POST /gobestads/operation/adsCustomerInitialSet/uploadPreview
     */
    function uploadInitialData(params: { file: File }) {
      const formData = new FormData()
      formData.append('file', params.file)

      return requestClient
        .post<UploadInitialDataResponse>(
          prefix('adsCustomerInitialSet/uploadPreview'),
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        )
        .then((res) => {
          return {
            totalCount: res?.totalCount || 0,
            validCount: res?.validCount || 0,
            uploadId: res?.uploadId || '',
          }
        })
    }

    /**
     * 查询上传期初列表
     * POST /gobestads/operation/adsCustomerInitialSet/queryUploadPreviewList
     */
    function queryUploadPreviewList(
      params: PagingRequest<QueryUploadPreviewListParams>,
    ) {
      return requestClient
        .post(prefix('adsCustomerInitialSet/queryUploadPreviewList'), {
          condition: {
            uploadId: params.condition.uploadId || '',
          },
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
        })
        .then((res) => {
          return {
            list: (res.list || []).map((item: UploadPreviewListItem) => ({
              customerId: item.customerId || '',
              customerName: item.customerName || '',
              initialIncome: item.initialIncome || 0,
              initialRecharge: item.initialRecharge || 0,
              initialSpend: item.initialSpend || 0,
              initialOtherFee: item.initialOtherFee || 0,
              initialDate: item.initialDate || '',
              isValid: item.isValid ?? false,
              reason: item.reason || '',
              isFirstAdd: item.isFirstAdd || '',
            })),
            total: res.total || 0,
          }
        })
    }

    /**
     * 确认上传期初数据
     * POST /gobestads/operation/adsCustomerInitialSet/confirmUpload
     */
    function confirmUpload(params: ConfirmUploadParams) {
      return requestClient
        .post<ConfirmUploadResponse>(
          prefix('adsCustomerInitialSet/confirmUpload'),
          {
            uploadId: params.uploadId,
          },
        )
        .then((res) => {
          return {
            successCount: res?.successCount || 0,
            totalCount: res?.totalCount || 0,
          }
        })
    }

    /**
     * 编辑期初数据
     * POST /gobestads/operation/adsCustomerInitialSet/update
     */
    function updateInitialData(params: UpdateInitialDataParams) {
      return requestClient.post(prefix('adsCustomerInitialSet/update'), {
        id: params.id,
        initialIncome: params.initialIncome,
        initialRecharge: params.initialRecharge,
        initialSpend: params.initialSpend,
        initialOtherFee: params.initialOtherFee,
        initialDate: params.initialDate,
      })
    }

    /**
     * 删除期初数据
     * POST /gobestads/operation/adsCustomerInitialSet/delete
     */
    function deleteInitialData(params: DeleteInitialDataParams) {
      return requestClient.post(prefix('adsCustomerInitialSet/delete'), {
        ids: params.ids || [],
      })
    }

    /**
     * 期初列表
     * POST /gobestads/operation/adsCustomerInitialSet/query
     */
    function getInitialList(params: PagingRequest<InitialListCondition>) {
      return requestClient
        .post(prefix('adsCustomerInitialSet/query'), {
          condition: {
            customerId: params.condition.customerId,
            customerName: params.condition.customerName || '',
          },
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
        })
        .then((res) => {
          return {
            list: (res.list || []).map((item: InitialListItem) => ({
              id: item.id,
              customerId: item.customerId,
              customerName: item.customerName || '',
              initialIncome: item.initialIncome,
              initialRecharge: item.initialRecharge,
              initialSpend: item.initialSpend,
              initialOtherFee: item.initialOtherFee,
              initialDate: item.initialDate,
              updatedBy: item.updatedBy,
              updatedTime: item.updatedTime,
            })),
            total: res.total || 0,
            pageIndex: res.pageIndex || params.pageIndex,
            pageSize: res.pageSize || params.pageSize,
          }
        })
    }

    /**
     * 客户对账日报列表
     * POST /gobestads/adsCustomerInitialSet/queryStatementReport
     */
    function getStatementReportList(
      params: PagingRequest<StatementReportListParams>,
    ) {
      return requestClient
        .post(prefix('adsCustomerInitialSet/queryStatementReport'), {
          condition: {
            customerId: params.condition.customerId,
            customerName: params.condition.customerName || '',
            reportDate: params.condition.reportDate || '',
          },
          pageIndex: params.pageIndex || 1,
          pageSize: params.pageSize || 20,
        })
        .then((res) => {
          return {
            list: (res.list || []).map((item: StatementReportListItem) => ({
              statDate: item.statDate,
              customerId: item.customerId,
              customerName: item.customerName,
              customerStatus: String(item.customerStatus),
              initialIncome: item.initialIncome,
              initialRecharge: item.initialRecharge,
              initialSpend: item.initialSpend,
              initialOtherFee: item.initialOtherFee,
              totalIncome: item.totalIncome,
              totalRecharge: item.totalRecharge,
              totalOtherFee: item.totalOtherFee,
              totalOurCompanyServiceFee: item.totalOurCompanyServiceFee,
              totalAgencyServiceFee: item.totalAgencyServiceFee,
              totalSpend: item.totalSpend,
              totalPrepaidPoolIn: item.totalPrepaidPoolIn,
              totalTaxDeducted: item.totalTaxDeducted,
              monthIncome: item.monthIncome,
              monthRecharge: item.monthRecharge,
              monthOtherFee: item.monthOtherFee,
              monthOurCompanyServiceFee: item.monthOurCompanyServiceFee,
              monthAgencyServiceFee: item.monthAgencyServiceFee,
              monthSpend: item.monthSpend,
              monthPrepaidPoolIn: item.monthPrepaidPoolIn,
              monthTaxDeducted: item.monthTaxDeducted,
              accumulatedUnrechargedBalance: item.accumulatedUnrechargedBalance,
              accumulatedSpendBalance: item.accumulatedSpendBalance,
            })),
            total: res.total,
          }
        })
    }

    /**
     * 导出客户对账日报
     * POST /gobestads/adsCustomerInitialSet/exportStatementReport
     */
    function exportStatementReport(params: ExportStatementReportParams) {
      return requestClient
        .post<ExportStatementReportResponse>(
          prefix('adsCustomerInitialSet/exportStatementReport'),
          {
            condition: {
              customerId: params.condition.customerId,
              customerName: params.condition.customerName || '',
              reportDate: params.condition.reportDate || '',
            },
          },
        )
        .then((res) => {
          return {
            fileUrl: res?.url || '',
            fileName: `客户对账日报_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`,
          }
        })
    }

    return {
      downloadTemplate,
      uploadInitialData,
      queryUploadPreviewList,
      confirmUpload,
      updateInitialData,
      deleteInitialData,
      getInitialList,
      getStatementReportList,
      exportStatementReport,
    }
  },
)
